/** Locale-aware formatting for loyalty. Locale follows the active i18next language. */

import i18n from '@/shared/i18n/i18n';

/**
 * Map an i18next 2-/5-letter locale code to a full BCP-47 tag that Intl accepts.
 * Falls back to 'en-US' for any unrecognised code.
 */
function getIntlLocale(): string {
  const lng = i18n.language;
  const map: Record<string, string> = {
    en: 'en-US',
    ru: 'ru-RU',
    'zh-CN': 'zh-CN',
    'zh-TW': 'zh-TW',
  };
  return map[lng] ?? 'en-US';
}

/** Format a point balance / threshold (thousands separated), or an em dash when absent. */
export function formatPoints(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale()).format(value);
}

/** Format a signed ledger delta (`+250` / `−100`), or an em dash when absent. */
export function formatSignedPoints(value?: number | null): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat(getIntlLocale(), { signDisplay: 'exceptZero' }).format(value);
}

/** Format an ISO timestamp, or an em dash when absent/invalid. */
export function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(getIntlLocale(), {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}
